import { useEffect } from 'react'

import { toggleArray } from '@domain-group/javascript-utils'
import { useForm, useSetState } from '@domain-group/react-hooks'
import type { UseFormPayload } from '@domain-group/react-hooks'

import { formatShippingAddress } from '@functions/address'

import { validateOrderItem } from '@functions/orderItem'
import type { ValidateOrderItemError } from '@functions/orderItem'

import useAddresses from '@hooks/useAddresses'
import useOrder from '@hooks/useOrder'
import useProviderAccounts from '@hooks/useProviderAccounts'
import useProviderDisclaimers from '@hooks/useProviderDisclaimers'
import useSelector from '@hooks/useSelector'

import defaultFormState, { requiredFields } from '@models/order'

import * as templatePageActions from '@redux/modules/templatePage'

import type { EntitiesState } from '@redux/entities'
import type {
  AgencyModel, ArtworkModel, OrderItemModel, OrderModel, ProviderAccountModel, ProviderDisclaimerModel,
} from '@models/types'

type DefaultState = {
  acceptedDisclaimerIds: number[],
  artworkIdsRequireDownload: number[],
  selectedAddressId: number | string | undefined,
}

const defaultState: DefaultState = {
  acceptedDisclaimerIds: [],
  artworkIdsRequireDownload: [],
  selectedAddressId: undefined,
}

type SetState = (state: Partial<DefaultState>) => void

type ArtworkTemplatePageIds = {
  combined: number[],
  [key: number]: number[],
}

type CheckTemplatePageUsageParams = {
  artworkTemplatePageIds: ArtworkTemplatePageIds,
  setState: SetState,
}

const checkTemplatePageUsage = async (params: CheckTemplatePageUsageParams) => {
  const { artworkTemplatePageIds, setState } = params
  const { checkTemplatePageUsage: checkUsageFn } = templatePageActions

  const { combined: templatePageIds } = artworkTemplatePageIds

  // @ts-expect-error TS2339
  const { data, success } = await checkUsageFn(templatePageIds)

  if (success && data){
    const unusedPageIds = data.unused_page_ids || []

    const artworkIdsRequireDownload = Object.keys(artworkTemplatePageIds).filter((artworkId) => {
      // @ts-expect-error TS7015
      const templateIds = artworkTemplatePageIds[artworkId] || []
      // @ts-expect-error TS7006
      return artworkId !== 'combined' && !!templateIds.filter(id => unusedPageIds.includes(id)).length
    }).map(id => Number(id))

    setState({ artworkIdsRequireDownload })
  }
}

type CollectTemplatePageIdsParams = {
  artworks: ArtworkModel[],
  artworkPages: EntitiesState['artworkPages'],
}

const collectTemplatePageIds = (params: CollectTemplatePageIdsParams) => {
  const { artworks, artworkPages } = params

  const result = artworks.reduce((acc, artwork) => {
    const templatePageIds = artwork.pages ? artwork.pages.map((pageId) => {
      // @ts-expect-error TS2538
      const artworkPage = artworkPages[pageId]
      return artworkPage?.template_page_id
    }) : []

    const filteredTemplatePageIds = templatePageIds.filter(i => i)

    acc[artwork.id] = filteredTemplatePageIds

    const mergedIds = [...acc.combined, ...filteredTemplatePageIds]
    acc.combined = Array.from(new Set(mergedIds))

    return acc
  }, { combined: [] } as { combined: number[], [key: number]: number[] })

  return result
}

type FindEntityDisclaimerParams = {
  entityId: number,
  entityType: string,
  providerDisclaimers: ProviderDisclaimerModel[],
}

const findEntityDisclaimer = (params: FindEntityDisclaimerParams) => {
  const { providerDisclaimers, entityType, entityId } = params

  const entityDisclaimer = providerDisclaimers.find((disclaimer) => {
    const { entity_type, entity_id } = disclaimer
    return entity_type === entityType && entity_id === entityId
  })

  return entityDisclaimer
}

type FindOrderDisclaimersParams = {
  orderItems: OrderItemModel[],
  providerDisclaimers: ProviderDisclaimerModel[],
}

const findOrderDisclaimers = (params: FindOrderDisclaimersParams) => {
  const { orderItems, providerDisclaimers } = params

  const disclaimers = {
    ids: [],
    requiredIds: [],
    notRequiredIds: [],
  }

  orderItems.forEach((orderItem) => {
    const { service_product_id } = orderItem

    const entityDisclaimer = findEntityDisclaimer({
      // @ts-expect-error TS2322
      entityId: service_product_id,
      entityType: 'ServiceProduct',
      providerDisclaimers,
    })

    if (entityDisclaimer){
      const { acceptance_required, id } = entityDisclaimer
      // @ts-expect-error TS2345
      if (!disclaimers.ids.includes(id)) disclaimers.ids.push(id)

      const key = acceptance_required ? 'requiredIds' : 'notRequiredIds'
      // @ts-expect-error TS2345
      if (!disclaimers[key].includes(id)) disclaimers[key].push(id)
    }
  })

  return disclaimers
}

type FindProviderAccountParams = {
  order: OrderModel,
  providerAccounts: ProviderAccountModel[],
}

const findProviderAccount = (params: FindProviderAccountParams) => {
  const {
    order: {
      agency_id,
      provider_id,
    },
    providerAccounts,
  } = params

  const account = providerAccounts.find(providerAccount => providerAccount.provider_id === provider_id
    && providerAccount.agency_id === agency_id)

  return account || {}
}

type SelectAddressIdParams = {
  addressId: number | string | 'physical_address',
  addresses: EntitiesState['addresses'],
  agency: AgencyModel,
  order: OrderModel,
  setFormState: UseFormPayload['setEntityState'],
  setState: SetState,
  updateOrderFn: (order: Partial<OrderModel>) => void,
}

const selectAddressId = (params: SelectAddressIdParams) => {
  const {
    addressId,
    addresses,
    agency,
    order,
    setFormState,
    setState,
    updateOrderFn,
  } = params

  const formattedAddress = formatShippingAddress({
    addressId,
    addresses,
    agency,
  })

  setState({ selectedAddressId: addressId })
  setFormState({ shipping_address: formattedAddress })

  updateOrderFn({ id: order.id, shipping_address: formattedAddress })
}

type ValidateOrderItemParams = {
  agency: AgencyModel,
  artworkIdsRequireDownload?: number[],
  entities: {
    artworkPages: EntitiesState['artworkPages'],
    artworks: EntitiesState['artworks'],
    orderItemTasks: EntitiesState['orderItemTasks'],
    serviceProducts: EntitiesState['serviceProducts'],
    services: EntitiesState['services'],
  },
  orderItems: OrderItemModel[],
  requireArtworkDataSync?: boolean,
}

type Errors = {
  blockers?: ValidateOrderItemError[],
  combined: ValidateOrderItemError[],
  [key: number]: ValidateOrderItemError[],
}

const validateOrderItems = (params: ValidateOrderItemParams) => {
  const {
    agency, artworkIdsRequireDownload, entities, orderItems,
  } = params

  const errors: Errors = {
    combined: [],
  }

  orderItems.forEach((orderItem) => {
    const itemErrors = validateOrderItem({
      artworkIdsRequireDownload,
      entities,
      orderItem,
      requireArtworkDataSync: agency.options?.require_artwork_data_sync || false,
    })

    if (itemErrors.length){
      errors.combined = [...errors.combined, ...itemErrors]
      errors[orderItem.id] = itemErrors
    }
  })

  errors.blockers = errors.combined.filter(error => error.blocker)

  return errors
}

function useOrderApproval(order: OrderModel) {
  const [state, setState] = useSetState(defaultState)
  const {
    acceptedDisclaimerIds,
    artworkIdsRequireDownload,
    selectedAddressId,
  } = state

  const orderPayload = useOrder(order)
  const {
    agency,
    callbacks: { updateOrder },
    filteredArtworks,
    filteredOrderItems,
    isApprovable,
    provider,
  } = orderPayload

  const entities = useSelector(reduxState => reduxState.entities)
  const {
    addresses,
    artworkPages,
    providerDisclaimers,
  } = entities

  const orderForm = useForm(defaultFormState, { entity: order, requiredFields }, [order.id])
  const {
    entityState,
    entityState: {
      purchase_order,
      shipping_address,
    },
    setEntityState,
  } = orderForm

  // Set Default Shipping Address
  useEffect(() => {
    if (!order.shipping_address){
      setEntityState({ shipping_address: agency.shipping_address })
    }
  }, [order.id])

  const artworkTemplatePageIds = collectTemplatePageIds({
    artworks: filteredArtworks,
    artworkPages,
  })

  useEffect(() => {
    checkTemplatePageUsage({ artworkTemplatePageIds, setState })
  }, [artworkTemplatePageIds.combined.length])

  const errors = validateOrderItems({
    // @ts-expect-error TS2740
    agency,
    artworkIdsRequireDownload,
    entities,
    orderItems: filteredOrderItems,
  })

  // Addresses
  const addressesPayload = useAddresses({
    filters: {
      owner_id: agency.id,
      owner_type: 'Agency',
    },
    performHttpRequests: !!agency.id,
  })

  const { filteredAddresses, loaded: loadedAddresses } = addressesPayload

  // Provider Accounts
  const providerAccountsPayload = useProviderAccounts({
    filters: {
      agency_id: agency.id,
      provider_id: provider.id,
    },
    performHttpRequests: !!agency.id && !!provider.id,
    requestOptions: {
      include_provider_account_locked_status: true,
    },
  })

  const { filteredProviderAccounts } = providerAccountsPayload

  // Provider Disclaimers
  const providerDisclaimersPayload = useProviderDisclaimers({
    filters: {
      provider_id: provider.id,
    },
  })

  const { filteredProviderDisclaimers } = providerDisclaimersPayload

  const orderDisclaimers = findOrderDisclaimers({
    orderItems: filteredOrderItems,
    providerDisclaimers: filteredProviderDisclaimers,
  })
  const hasOrderDisclaimers = orderDisclaimers.ids.some(id => !!providerDisclaimers[id])
  const disclaimersAccepted = acceptedDisclaimerIds.length === orderDisclaimers.requiredIds.length

  const providerAccount = findProviderAccount({
    order,
    providerAccounts: filteredProviderAccounts,
  })

  // @ts-expect-error TS2339
  const cashPaymentRequired = providerAccount.account_locked || providerAccount.terms === 'cash'

  return {
    ...orderPayload,
    acceptedDisclaimerIds,
    approveOrderParams: {
      purchase_order,
      shipping_address,
    },
    callbacks: {
      ...orderPayload.callbacks,
      removeDownloadedArtworkId: (artworkId: number) => setState({
        artworkIdsRequireDownload: artworkIdsRequireDownload.filter(id => id !== artworkId),
      }),
      selectAddressId: (addressId: number | string | 'physical_address') => (
        selectAddressId({
          addressId,
          // @ts-expect-error TS2322
          agency,
          addresses,
          order,
          updateOrderFn: updateOrder,
          setFormState: setEntityState,
          setState,
        })
      ),
      setEntityState,
      setState,
      toggleDisclaimerId: (disclaimerId: number) => setState({
        acceptedDisclaimerIds: toggleArray(acceptedDisclaimerIds, disclaimerId),
      }),
    },
    cashPaymentRequired,
    disclaimersAccepted,
    entityState,
    errors,
    filteredAddresses,
    hasOrderDisclaimers,
    isApprovable: !errors.blockers?.length && disclaimersAccepted && isApprovable,
    loadedAddresses,
    orderDisclaimers,
    providerAccount,
    selectedAddressId,
  }
}

export type UseOrderApprovalPayload = ReturnType<typeof useOrderApproval>

export default useOrderApproval
